import React, { useEffect, useRef, useState } from 'react';
import { Mail, Award, GraduationCap, Users, Sparkles } from 'lucide-react';
import { principalInvestigator, teamMembers, teamCategories, labAlumni } from '../data/team';

const Team = () => {
  const sectionRef = useRef(null);
  const [selectedCategory, setSelectedCategory] = useState('All');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal').forEach((el, index) => {
              setTimeout(() => {
                el.classList.add('active');
              }, index * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const filteredMembers = selectedCategory === 'All' 
    ? teamMembers 
    : teamMembers.filter(member => member.category === selectedCategory);

  return (
    <section ref={sectionRef} id="team" className="py-28 bg-white relative overflow-hidden">
      {/* Premium background decoration */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-indigo-100/40 to-transparent rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-cyan-100/40 to-transparent rounded-full blur-3xl"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full glass-light border border-indigo-200 mb-8 shadow-lg shadow-indigo-100/50">
            <Users className="h-5 w-5 text-indigo-600" />
            <span className="text-sm font-bold text-indigo-700 tracking-wide">OUR TEAM</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 leading-tight" style={{ fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '-0.02em' }}>
            Meet the <span className="gradient-text">Team</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-indigo-600 via-cyan-500 to-amber-500 mx-auto rounded-full mb-4"></div>
        </div>

        {/* Principal Investigator */}
        <div className="mb-24">
          <div className="glass-light rounded-[32px] overflow-hidden shadow-2xl border-2 border-indigo-100 hover-glow">
            <div className="grid md:grid-cols-5 gap-0">
              {/* PI Photo */}
              <div className="md:col-span-2 relative overflow-hidden bg-gradient-to-br from-indigo-100 to-cyan-100 flex items-center justify-center p-8">
                <img 
                  src={principalInvestigator.photo} 
                  alt={principalInvestigator.name}
                  className="w-56 h-56 rounded-full object-cover shadow-2xl border-4 border-white"
                />
                <div className="absolute top-6 right-6 gold-gradient px-4 py-2 rounded-full shadow-lg">
                  <span className="text-sm font-bold text-white">Principal Investigator</span>
                </div>
              </div>
              
              {/* PI Info */}
              <div className="md:col-span-3 p-10 md:p-12 flex items-center">
                <div>
                  <h3 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    {principalInvestigator.name}, {principalInvestigator.credentials}
                  </h3>
                  <p className="text-xl text-indigo-600 font-semibold">{principalInvestigator.fullTitle}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-16">
          <button
            onClick={() => setSelectedCategory('All')}
            className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
              selectedCategory === 'All'
                ? 'premium-gradient text-white shadow-lg shadow-indigo-500/30'
                : 'glass-light text-slate-700 hover:border-indigo-300'
            }`}
          >
            All Team
          </button>
          {teamCategories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                selectedCategory === category
                  ? 'premium-gradient text-white shadow-lg shadow-indigo-500/30'
                  : 'glass-light text-slate-700 hover:border-indigo-300'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Team Members Grid */}
        <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-8">
          {filteredMembers.map((member, index) => (
            <div 
              key={member.id}
              className="group"
            >
              <div className="glass-light rounded-2xl overflow-hidden shadow-lg hover-lift border border-indigo-100/50 transition-all duration-300">
                {/* Member Photo */}
                <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-indigo-100 to-cyan-100">
                  <img 
                    src={member.photo} 
                    alt={member.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                
                {/* Member Info */}
                <div className="p-6">
                  <h4 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-indigo-600 transition-colors" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    {member.name}
                  </h4>
                  <p className="text-sm md:text-base text-black font-bold mb-3">{member.title}</p>
                  <div className="pt-3 border-t border-indigo-100">
                    <span className="text-xs font-semibold text-indigo-600 uppercase tracking-wide">
                      {member.category}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Lab Alumni Section */}
        <div className="mt-32">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full glass-light border border-amber-200 mb-8 shadow-lg shadow-amber-100/50">
              <Award className="h-5 w-5 text-amber-600" />
              <span className="text-sm font-bold text-amber-700 tracking-wide">LAB ALUMNI</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 leading-tight" style={{ fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '-0.02em' }}>
              Our <span className="gradient-text">Alumni</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-600 via-orange-500 to-amber-500 mx-auto rounded-full mb-4"></div>
            <p className="text-base md:text-lg text-slate-700 max-w-3xl mx-auto leading-relaxed">
              Celebrating our former lab members and their continued success
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="glass-light rounded-2xl p-8 md:p-12 border-2 border-amber-100 shadow-xl">
              <div className="space-y-6">
                {labAlumni.map((member, index) => (
                  <div 
                    key={member.id}
                    className={`flex items-center justify-between py-4 ${
                      index !== labAlumni.length - 1 ? 'border-b border-amber-100' : ''
                    }`}
                  >
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                        {member.name}
                      </h4>
                      <p className="text-sm md:text-base text-slate-600">{member.title}</p>
                    </div>
                    <div className="text-right ml-6">
                      <span className="inline-flex items-center px-4 py-2 rounded-lg bg-gradient-to-r from-amber-100 to-orange-100 border border-amber-200">
                        <span className="text-lg font-bold text-amber-700">{member.year}</span>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Join Us CTA */}
        <div className="mt-24">
          <div className="glass-light rounded-[32px] p-12 md:p-16 text-center border-2 border-indigo-100 shadow-xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-indigo-500/10 to-transparent rounded-full"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-cyan-500/10 to-transparent rounded-full"></div>
            
            <div className="relative z-10">
              <Sparkles className="h-12 w-12 text-indigo-600 mx-auto mb-6" />
              <h3 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Join Our Team
              </h3>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8 font-light">
                We're always looking for talented and passionate researchers to join our lab. 
                If you're interested in systems biology, synthetic circuits, or translational medicine, we'd love to hear from you.
              </p>
              <a 
                href="#contact"
                className="inline-flex items-center gap-2 px-8 py-4 premium-gradient text-white rounded-xl font-bold hover:shadow-lg hover:shadow-indigo-500/50 transition-all duration-300 text-lg"
              >
                <GraduationCap className="h-6 w-6" />
                Get in Touch
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Team;